/**
 * Thin Promise wrapper around Telegram.WebApp.DeviceStorage.
 *
 * DeviceStorage (Bot API 9.0) is a per-bot, per-user key/value store that
 * survives the user swiping the Web App window away — useful so a
 * mid-edit selection isn't lost on accidental dismiss.
 *
 * The calendar uses a *set* of ISO timestamps now (the user can mark
 * many slots), so we serialise as JSON. Legacy single-string values
 * from older bundles are tolerated transparently for backwards-compat.
 */

const NS = "gennety.calendar";

function storage(): TelegramWebAppDeviceStorage | null {
  const tg = window.Telegram?.WebApp;
  if (!tg?.DeviceStorage) return null;
  // DeviceStorage is Bot API 9.0. On older clients the namespace can be
  // present but its callbacks never fire — which hangs any awaiting caller
  // (e.g. the onboarding boot path stuck forever on "Synchronizing"). Gate on
  // the reported client version and fall back to localStorage when unsupported.
  if (typeof tg.isVersionAtLeast === "function" && !tg.isVersionAtLeast("9.0")) {
    return null;
  }
  return tg.DeviceStorage;
}

// Belt-and-suspenders: even a client that reports >= 9.0 can fail to invoke a
// DeviceStorage callback. Any awaited read on a boot/critical path must never
// hang, so we race the callback against a short timeout.
const DEVICE_STORAGE_TIMEOUT_MS = 2000;

function key(matchId: string): string {
  return `${NS}.match.${matchId}`;
}

// Onboarding visual-animation progress. There is one active onboarding run per
// user, so a static key (DeviceStorage is already per-bot/per-user) is enough.
const ONBOARDING_VISUAL_KEY = "gennety.onboarding.visual";

// Welcome-mascot once-marker. Deliberately a SECOND key rather than another
// sentinel on the visual-progress one: that value is a scene index the router
// clamps and resumes from, and folding a boolean into it would make every
// arithmetic read of it ambiguous. Same no-versioning rule as above — a lost
// flag replays a 3.7s greeting once, which is bounded and rare.
const ONBOARDING_WELCOME_KEY = "gennety.onboarding.welcomed";

// Snapshot of the peer's slot set at the user's last successful save.
// Used to NEW-badge slots the peer has added since the user last "acted".
function peerSeenKey(matchId: string): string {
  return `${NS}.match.${matchId}.peer-seen`;
}

export async function savePickedSet(matchId: string, isos: string[]): Promise<void> {
  const value = JSON.stringify(isos);
  const ds = storage();
  if (!ds) {
    try {
      window.localStorage.setItem(key(matchId), value);
    } catch {
      // ignore
    }
    return;
  }
  await new Promise<void>((resolve) => {
    ds.setItem(key(matchId), value, (err) => {
      if (err) console.warn("DeviceStorage setItem failed:", err);
      resolve();
    });
  });
}

export async function loadPickedSet(matchId: string): Promise<string[] | null> {
  const ds = storage();
  const raw = ds
    ? await new Promise<string | null>((resolve) => {
        ds.getItem(key(matchId), (err, value) => {
          if (err) {
            console.warn("DeviceStorage getItem failed:", err);
            resolve(null);
            return;
          }
          resolve(value ?? null);
        });
      })
    : (() => {
        try {
          return window.localStorage.getItem(key(matchId));
        } catch {
          return null;
        }
      })();

  if (raw === null) return null;
  // Legacy: a single ISO string from the previous Mini App bundle.
  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.every((x) => typeof x === "string")
        ? normalizeIsoList(parsed)
        : null;
    } catch {
      return null;
    }
  }
  return normalizeIsoList([raw]);
}

export async function savePeerSeen(matchId: string, isos: string[]): Promise<void> {
  const value = JSON.stringify(isos);
  const ds = storage();
  if (!ds) {
    try {
      window.localStorage.setItem(peerSeenKey(matchId), value);
    } catch {
      // ignore
    }
    return;
  }
  await new Promise<void>((resolve) => {
    ds.setItem(peerSeenKey(matchId), value, (err) => {
      if (err) console.warn("DeviceStorage setItem failed:", err);
      resolve();
    });
  });
}

export async function loadPeerSeen(matchId: string): Promise<string[] | null> {
  const ds = storage();
  const raw = ds
    ? await new Promise<string | null>((resolve) => {
        ds.getItem(peerSeenKey(matchId), (err, value) => {
          if (err) {
            console.warn("DeviceStorage getItem failed:", err);
            resolve(null);
            return;
          }
          resolve(value ?? null);
        });
      })
    : (() => {
        try {
          return window.localStorage.getItem(peerSeenKey(matchId));
        } catch {
          return null;
        }
      })();

  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return null;
  }
}

export async function clearPicked(matchId: string): Promise<void> {
  const ds = storage();
  if (!ds) {
    try {
      window.localStorage.removeItem(key(matchId));
    } catch {
      // ignore
    }
    return;
  }
  await new Promise<void>((resolve) => {
    ds.removeItem(key(matchId), () => resolve());
  });
}

/**
 * Persist the user's current position in the onboarding visual animation so a
 * swipe-away-and-return resumes on the same scene instead of replaying from 0.
 * Stored as a plain integer (scene index, or the VISUAL_DONE sentinel).
 */
export async function saveOnboardingProgress(progress: number): Promise<void> {
  const value = String(Math.floor(progress));
  const ds = storage();
  if (!ds) {
    try {
      window.localStorage.setItem(ONBOARDING_VISUAL_KEY, value);
    } catch {
      // ignore
    }
    return;
  }
  await new Promise<void>((resolve) => {
    ds.setItem(ONBOARDING_VISUAL_KEY, value, (err) => {
      if (err) console.warn("DeviceStorage setItem failed:", err);
      resolve();
    });
  });
}

export async function loadOnboardingProgress(): Promise<number | null> {
  const ds = storage();
  const raw = ds
    ? await new Promise<string | null>((resolve) => {
        let settled = false;
        const finish = (value: string | null): void => {
          if (settled) return;
          settled = true;
          resolve(value);
        };
        // A client that exposes DeviceStorage but never calls back must not
        // hang the onboarding boot ("Synchronizing" forever). Fall back to
        // "no stored progress" after a short wait — resume is a nice-to-have.
        const timer = setTimeout(() => finish(null), DEVICE_STORAGE_TIMEOUT_MS);
        ds.getItem(ONBOARDING_VISUAL_KEY, (err, value) => {
          clearTimeout(timer);
          if (err) {
            console.warn("DeviceStorage getItem failed:", err);
            finish(null);
            return;
          }
          finish(value ?? null);
        });
      })
    : (() => {
        try {
          return window.localStorage.getItem(ONBOARDING_VISUAL_KEY);
        } catch {
          return null;
        }
      })();

  if (raw === null || raw.trim() === "") return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export async function clearOnboardingProgress(): Promise<void> {
  const ds = storage();
  if (!ds) {
    try {
      window.localStorage.removeItem(ONBOARDING_VISUAL_KEY);
    } catch {
      // ignore
    }
    return;
  }
  await new Promise<void>((resolve) => {
    ds.removeItem(ONBOARDING_VISUAL_KEY, () => resolve());
  });
}

function normalizeIsoList(values: string[]): string[] | null {
  const normalized = values.filter((value) => {
    if (value.trim() === "") return false;
    return !Number.isNaN(new Date(value).getTime());
  });
  return normalized.length > 0 ? normalized : null;
}

/**
 * Read the welcome-mascot once-marker.
 *
 * This runs on the onboarding boot path, so it carries the same timeout race
 * as `loadOnboardingProgress` — a client that exposes DeviceStorage and never
 * calls back must not hang the app on its loading screen. Failing to
 * `true` rather than `false` is the deliberate direction: if we cannot tell
 * whether the user has been greeted, do not greet them again.
 */
export async function loadWelcomeSeen(): Promise<boolean> {
  const ds = storage();
  const raw = ds
    ? await new Promise<string | null>((resolve) => {
        let settled = false;
        const finish = (value: string | null): void => {
          if (settled) return;
          settled = true;
          resolve(value);
        };
        const timer = setTimeout(() => finish("1"), DEVICE_STORAGE_TIMEOUT_MS);
        ds.getItem(ONBOARDING_WELCOME_KEY, (err, value) => {
          clearTimeout(timer);
          if (err) {
            console.warn("DeviceStorage getItem failed:", err);
            finish("1");
            return;
          }
          finish(value ?? null);
        });
      })
    : (() => {
        try {
          return window.localStorage.getItem(ONBOARDING_WELCOME_KEY);
        } catch {
          return null;
        }
      })();

  return raw !== null && raw.trim() !== "";
}

/**
 * Mark the welcome mascot as played. Fire-and-forget by design: the greeting
 * has already happened by the time this is called, so a failed write costs one
 * replay on the next launch and must never delay the screen underneath.
 */
export async function saveWelcomeSeen(): Promise<void> {
  const ds = storage();
  if (!ds) {
    try {
      window.localStorage.setItem(ONBOARDING_WELCOME_KEY, "1");
    } catch {
      // ignore
    }
    return;
  }
  await new Promise<void>((resolve) => {
    ds.setItem(ONBOARDING_WELCOME_KEY, "1", (err) => {
      if (err) console.warn("DeviceStorage setItem failed:", err);
      resolve();
    });
  });
}
